import { useMemo } from 'react';
import {
  PlusCircle,
  Trash2,
  RefreshCw,
  AlertCircle,
  CheckCircle2,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import HelpTooltip from '@/components/ui/help-tooltip';
import { DatasetCsvUploadMetadata } from '@/types/source-types';
import { MultiSelect } from '@/components/ui/multi-select';

export const createDatasetForeignKeyId = () =>
  Math.random().toString(36).slice(2, 10);

export interface DatasetOption {
  value: string;
  label: string;
}

export interface DatasetCsvForeignKeyForm {
  id: string;
  columns: string[];
  datasetId?: string;
  table?: string;
  referencedColumns: string[];
}

export interface DatasetCsvSelection {
  key: string;
  primaryKey: string[];
  foreignKeys: DatasetCsvForeignKeyForm[];
}

export interface DatasetCsvForeignKeyError {
  id: string;
  columns?: string;
  datasetId?: string;
  referencedColumns?: string;
}

export interface DatasetCsvSelectionError {
  primaryKey?: string;
  general?: string;
  foreignKeys?: DatasetCsvForeignKeyError[];
}

interface DatasetCsvKeyEditorProps {
  displayName: string;
  metadata: DatasetCsvUploadMetadata | null;
  isLoadingMetadata: boolean;
  metadataError?: string;
  onRetryMetadata?: () => void;
  value: DatasetCsvSelection;
  onChange: (value: DatasetCsvSelection) => void;
  datasetOptions: DatasetOption[];
  disabled?: boolean;
  error?: DatasetCsvSelectionError;
  onApplyToAll?: (selection: DatasetCsvSelection) => void;
  showApplyToAll?: boolean;
  wasAppliedToAll?: boolean;
}

export const DatasetCsvKeyEditor = ({
  displayName,
  metadata,
  isLoadingMetadata,
  metadataError,
  onRetryMetadata,
  value,
  onChange,
  datasetOptions,
  disabled,
  error,
  onApplyToAll,
  showApplyToAll,
  wasAppliedToAll,
}: DatasetCsvKeyEditorProps) => {
  const columns = useMemo(
    () =>
      metadata?.headers?.filter((header): header is string =>
        Boolean(header),
      ) || [],
    [metadata?.headers],
  );

  const primaryKeySuggestions = useMemo(() => {
    const suggestions =
      metadata?.primary_key_suggestions ||
      metadata?.primary_key ||
      metadata?.suggested_primary_keys;
    return Array.isArray(suggestions)
      ? suggestions.filter(
          (entry): entry is string => typeof entry === 'string',
        )
      : [];
  }, [
    metadata?.primary_key,
    metadata?.primary_key_suggestions,
    metadata?.suggested_primary_keys,
  ]);

  const handlePrimaryKeyChange = (primaryKey: string[]) => {
    onChange({
      ...value,
      primaryKey,
    });
  };

  const handleForeignKeyChange = (
    index: number,
    next: DatasetCsvForeignKeyForm,
  ) => {
    const newForeignKeys = value.foreignKeys.map((item, idx) =>
      idx === index ? next : item,
    );
    onChange({
      ...value,
      foreignKeys: newForeignKeys,
    });
  };

  const handleRemoveForeignKey = (index: number) => {
    const nextForeignKeys = value.foreignKeys.filter((_, idx) => idx !== index);
    onChange({
      ...value,
      foreignKeys: nextForeignKeys,
    });
  };

  const handleAddForeignKey = () => {
    const next: DatasetCsvForeignKeyForm = {
      id: createDatasetForeignKeyId(),
      columns: [],
      referencedColumns: [],
    };
    onChange({
      ...value,
      foreignKeys: [...value.foreignKeys, next],
    });
  };

  const applyPrimaryKeySuggestion = () => {
    if (!primaryKeySuggestions.length) return;
    handlePrimaryKeyChange(primaryKeySuggestions);
  };

  return (
    <div className='border rounded-md p-4 space-y-3 bg-muted/10 overflow-y-auto'>
      <div className='flex flex-wrap items-center justify-between gap-3'>
        <div className='space-y-1 min-w-0'>
          <p className='text-sm font-medium truncate'>{displayName}</p>
          {columns.length > 0 && (
            <p className='text-xs text-muted-foreground'>
              {columns.length} column{columns.length === 1 ? '' : 's'} detected
            </p>
          )}
          {metadataError && (
            <div className='flex items-center gap-2 text-xs text-red-500'>
              <AlertCircle className='h-4 w-4 shrink-0' />
              <span className='truncate'>{metadataError}</span>
              {onRetryMetadata && (
                <Button
                  type='button'
                  variant='link'
                  className='h-auto p-0 text-xs'
                  onClick={onRetryMetadata}
                >
                  Retry
                </Button>
              )}
            </div>
          )}
        </div>
        <div className='flex items-center gap-2 text-xs text-muted-foreground'>
          {showApplyToAll &&
            onApplyToAll &&
            (wasAppliedToAll ? (
              <Button
                type='button'
                variant='secondary'
                size='sm'
                className='h-7 text-xs gap-1 text-green-600'
                disabled
              >
                <CheckCircle2 className='size-3.5' /> Applied
              </Button>
            ) : (
              <Button
                type='button'
                variant='secondary'
                size='sm'
                className='h-7 text-xs'
                onClick={() => onApplyToAll(value)}
                disabled={disabled}
              >
                Apply to all
              </Button>
            ))}
          {isLoadingMetadata ? (
            <>
              <RefreshCw className='h-4 w-4 animate-spin' />
              <span>Loading metadata…</span>
            </>
          ) : metadata ? (
            <>
              <span>Metadata loaded</span>
              <HelpTooltip label='Metadata summary help'>
                <p>
                  Metadata summary pulled from the CSV sidecar (headers, row
                  counts). Refresh if the file has been updated.
                </p>
              </HelpTooltip>
            </>
          ) : null}
        </div>
      </div>

      <div className='space-y-2'>
        <div className='space-y-2'>
          <div className='flex items-center gap-2'>
            <Label className='text-sm font-medium'>Primary key columns</Label>
            <HelpTooltip label='Primary key columns help'>
              <p>
                Choose the column(s) that uniquely identify each row in this
                CSV. Required if you plan to join or update records reliably.
              </p>
            </HelpTooltip>
          </div>
          <MultiSelect<string>
            items={columns}
            selectedValues={value.primaryKey}
            onSelectionChange={handlePrimaryKeyChange}
            getItemValue={(column) => column}
            getItemLabel={(column) => column}
            placeholder='Select primary key columns'
            searchPlaceholder='Search columns...'
            emptyMessage='No columns found.'
            buttonClassName='w-full'
            singularLabel='column'
            pluralLabel='columns'
            disabled={disabled || isLoadingMetadata}
          />
          {value.primaryKey.length > 0 && (
            <div className='flex flex-wrap gap-2'>
              {value.primaryKey.map((column) => (
                <Badge
                  key={column}
                  variant='secondary'
                  className='text-xs font-normal px-2 py-1'
                >
                  {column}
                </Badge>
              ))}
            </div>
          )}
        </div>
        {primaryKeySuggestions.length > 0 && !value.primaryKey.length && (
          <Button
            type='button'
            size='sm'
            variant='ghost'
            onClick={applyPrimaryKeySuggestion}
            className='h-7 w-fit'
            disabled={disabled}
          >
            Use suggested columns
          </Button>
        )}
        {error?.primaryKey && (
          <p className='text-xs text-red-500'>{error.primaryKey}</p>
        )}
      </div>

      <div className='space-y-2'>
        <div className='flex items-center justify-between'>
          <Label className='text-sm font-medium'>Foreign keys</Label>
          <Button
            type='button'
            variant='outline'
            size='sm'
            onClick={handleAddForeignKey}
            disabled={disabled}
          >
            <PlusCircle className='h-4 w-4 mr-1' /> Add foreign key
          </Button>
        </div>
        {value.foreignKeys.length === 0 ? (
          <p className='text-xs text-muted-foreground'>
            No foreign keys configured for this file.
          </p>
        ) : (
          <div className='space-y-3'>
            {value.foreignKeys.map((foreignKey, index) => {
              const foreignKeyError = error?.foreignKeys?.find(
                (item) => item.id === foreignKey.id,
              );

              return (
                <div
                  key={foreignKey.id}
                  className='border rounded-md p-3 space-y-3 bg-background'
                >
                  <div className='flex items-center justify-between'>
                    <p className='text-xs font-medium text-muted-foreground'>
                      Foreign key #{index + 1}
                    </p>
                    <Button
                      type='button'
                      variant='ghost'
                      size='sm'
                      onClick={() => handleRemoveForeignKey(index)}
                      className='h-8'
                      disabled={disabled}
                    >
                      <Trash2 className='h-4 w-4' />
                    </Button>
                  </div>

                  <div className='space-y-2'>
                    <div className='flex items-center gap-2'>
                      <Label className='text-sm font-medium'>
                        Local columns
                      </Label>
                      <HelpTooltip label='Local columns help'>
                        <p>
                          Select the column(s) in this CSV that link to another
                          table.
                        </p>
                      </HelpTooltip>
                    </div>
                    <MultiSelect<string>
                      items={columns}
                      selectedValues={foreignKey.columns}
                      onSelectionChange={(columns) =>
                        handleForeignKeyChange(index, {
                          ...foreignKey,
                          columns,
                        })
                      }
                      getItemValue={(column) => column}
                      getItemLabel={(column) => column}
                      placeholder='Select local columns'
                      searchPlaceholder='Search columns...'
                      emptyMessage='No columns found.'
                      buttonClassName='w-full'
                      singularLabel='column'
                      pluralLabel='columns'
                      disabled={disabled || isLoadingMetadata}
                    />
                    {foreignKey.columns.length > 0 && (
                      <div className='flex flex-wrap gap-2'>
                        {foreignKey.columns.map((column) => (
                          <Badge
                            key={column}
                            variant='secondary'
                            className='text-xs font-normal px-2 py-1'
                          >
                            {column}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                  {foreignKeyError?.columns && (
                    <p className='text-xs text-red-500'>
                      {foreignKeyError.columns}
                    </p>
                  )}

                  <div className='grid gap-3 md:grid-cols-2'>
                    <div className='space-y-1'>
                      <div className='flex items-center gap-2'>
                        <Label className='text-sm font-medium'>
                          Target dataset
                        </Label>
                        <HelpTooltip label='Target dataset help'>
                          <p>
                            Pick the dataset that contains the referenced table.
                          </p>
                        </HelpTooltip>
                      </div>
                      <Select
                        value={foreignKey.datasetId}
                        onValueChange={(datasetId) =>
                          handleForeignKeyChange(index, {
                            ...foreignKey,
                            datasetId,
                          })
                        }
                        disabled={disabled || datasetOptions.length === 0}
                      >
                        <SelectTrigger className='w-full justify-between h-9 text-sm'>
                          <SelectValue
                            placeholder={
                              datasetOptions.length === 0
                                ? 'No available datasets'
                                : 'Select dataset'
                            }
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {datasetOptions.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {foreignKeyError?.datasetId && (
                        <p className='text-xs text-red-500'>
                          {foreignKeyError.datasetId}
                        </p>
                      )}
                    </div>
                    <div className='space-y-1'>
                      <div className='flex items-center gap-2'>
                        <Label className='text-sm font-medium'>
                          Target table
                        </Label>
                        <HelpTooltip label='Target table help'>
                          <p>
                            Leave blank to use the default table name derived
                            from the CSV. Set it only if you need a specific
                            name for SQL or catalog references.
                          </p>
                        </HelpTooltip>
                      </div>
                      <Input
                        placeholder='Table name (optional)'
                        value={foreignKey.table ?? ''}
                        onChange={(event) =>
                          handleForeignKeyChange(index, {
                            ...foreignKey,
                            table: event.target.value || undefined,
                          })
                        }
                        disabled={disabled}
                        className='h-9 text-sm'
                      />
                    </div>
                  </div>

                  <div className='space-y-1'>
                    <div className='flex items-center gap-2'>
                      <Label className='text-sm font-medium'>
                        Referenced columns
                      </Label>
                      <HelpTooltip label='Referenced columns help'>
                        <p>
                          List the column(s) in the referenced table that this
                          foreign key points to.
                        </p>
                      </HelpTooltip>
                    </div>
                    <Input
                      placeholder='Comma-separated column names'
                      value={foreignKey.referencedColumns.join(', ')}
                      onChange={(event) => {
                        const raw = event.target.value;
                        const nextColumns = raw
                          .split(',')
                          .map((entry) => entry.trim())
                          .filter(Boolean);
                        handleForeignKeyChange(index, {
                          ...foreignKey,
                          referencedColumns: nextColumns,
                        });
                      }}
                      disabled={disabled}
                      className='h-9 text-sm'
                    />
                    {foreignKeyError?.referencedColumns && (
                      <p className='text-xs text-red-500'>
                        {foreignKeyError.referencedColumns}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {error?.general && (
        <p className='text-xs text-red-500'>{error.general}</p>
      )}
    </div>
  );
};

export const buildDefaultSelection = (key: string): DatasetCsvSelection => ({
  key,
  primaryKey: [],
  foreignKeys: [],
});
